import React from 'react';
import { useTranslation } from 'react-i18next';
import { timeCostView } from 'src/enums/simulations';
import { HOURS_PER_DAY } from 'src/enums/general';
import { LineChart, BarChart, ScatterChart } from '@oliasoft-open-source/charts-library';
import { connect } from 'react-redux';
import { selectPrimaryCurrency } from 'store/entities/company-settings/selectors';
import { simulationChartType } from 'src/enums/compare-estimates';
import translations from 'src/internationalisation/translation-map.json';
import { displayNumber, round, unum } from '@oliasoft-open-source/units';
import { getLineColor } from 'src/views/simulations/simulations';
import { generateActualChartLine } from 'src/views/time-tracker/utils/generate-actual-chart-line';
import { itemFields } from 'src/enums/time-tracker';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';

const CombinedChart = ({
  chartType,
  probabilityTypes,
  timeCostType,
  estimates,
  primaryCurrency,
  controlsPortalId,
  depthUnit,
}) => {
  const { t } = useTranslation();
  const handleOnHover = (ev, di, i, gd) => {
    const { chart } = ev;
    const { x, y } = gd[di].data[i];
    const value = t(timeCostType === timeCostView.TIME ? `${t(translations.days)}` : `${primaryCurrency}`);

    const tooltipText = `P${y}: ${displayNumber(round(x, 2), {
      scientific: false,
    })} ${value}`;

    chart.config.options.plugins.tooltip.callbacks.label = (ctx) => {
      let label = ctx.dataset.label || '';
      if (label) {
        label = tooltipText;
      }
      return label;
    };

    chart.config.options.plugins.tooltip.callbacks.title = (ctx) => {
      let label = ctx[0].label || '';
      if (label) {
        label = '';
      }
      return label;
    };
  };

  const calculateCombinedCost = (data) => {
    if (data) {
      const sum = (arr) => {
        const allZeros = arr.every((num) => num === 0);
        if (allZeros) {
          return null;
        }
        return arr.reduce((acc, curr) => acc + curr, 0);
      };

      return round(sum(data), 2);
    } else return null;
  };

  let datasets;
  let labels;

  if (chartType === simulationChartType.TOTAL_COST) {
    const totalCosts = estimates
      .map((estimate) => {
        const costs = estimate.simulationResult?.cumulative_costs?.costs?.[probabilityTypes[0]] || [];
        const totalCost = costs.reduce((acc, cost) => Math.max(acc, cost), 0);
        return {
          cost: totalCost,
          label: estimate.name,
        };
      })
      .sort((a, b) => a.cost - b.cost);

    labels = totalCosts.map((totalCost) => totalCost.label);
    datasets = [
      {
        data: totalCosts.map((totalCost) => totalCost.cost),
        backgroundColor: totalCosts.map((_totalCost, index) => getLineColor(index, estimates.length)),
        borderColor: getLineColor(0, estimates.length),
      },
    ];
  } else {
    datasets = estimates
      .flatMap((estimate, estimateIndex) => {
        if (
          !estimate?.simulationResult?.[simulationChartType.OPERATION_VS_DEPTH] ||
          !estimate?.simulationResult?.[simulationChartType.OPERATION_COST_VS_DEPTH]
        ) {
          return [];
        }

        if (
          chartType === simulationChartType.CUMULATIVE_PROBABILITY ||
          chartType === simulationChartType.CUMULATIVE_PROBABILITY_COST
        ) {
          let data = [];
          const isTime = timeCostType === timeCostView.TIME;
          const chartData =
            estimate.simulationResult[
              isTime ? simulationChartType.CUMULATIVE_PROBABILITY : simulationChartType.CUMULATIVE_PROBABILITY_COST
            ];

          let dataKey;
          if (isTime) {
            dataKey = timeCostView.TIME;
          } else if (chartData.costs) {
            dataKey = timeCostView.COSTS;
          } else {
            dataKey = timeCostView.COST;
          }
          data = chartData[dataKey]?.map((item, i) => ({
            y: i,
            x: isTime ? item / HOURS_PER_DAY : item,
          }));

          const color = getLineColor(estimateIndex, estimates.length);
          return {
            borderColor: color,
            pointBackgroundColor: color,
            label: estimate.name,
            data,
          };
        }

        if (chartType === simulationChartType.OPERATION_VS_DEPTH) {
          const numProbabilities = probabilityTypes.length;
          return probabilityTypes.map((currentProbability, probIndex) => {
            const chartData =
              estimate.simulationResult[
                timeCostType === timeCostView.TIME
                  ? simulationChartType.OPERATION_VS_DEPTH
                  : simulationChartType.OPERATION_COST_VS_DEPTH
              ];

            const data =
              currentProbability === itemFields.ACTUAL && timeCostType === timeCostView.TIME
                ? generateActualChartLine(estimate.timeTracker, t, translations, depthUnit)?.data
                : chartData[currentProbability]?.depth.map((item, i) => ({
                    y: unum(item, depthUnit, 'm'),
                    x:
                      timeCostType === timeCostView.TIME
                        ? chartData[currentProbability][timeCostView.TIME][i] / HOURS_PER_DAY
                        : (chartData[currentProbability]?.costs?.[i] ?? chartData[currentProbability]?.cost?.[i]),
                  }));

            const colorIndex = estimateIndex * numProbabilities + probIndex;
            const color = getLineColor(colorIndex, numProbabilities * estimates.length);

            return {
              borderColor: color,
              pointBackgroundColor: color,
              label: `${estimate.name} - ${currentProbability.toUpperCase()}`,
              data,
            };
          });
        }

        if (chartType === simulationChartType.SIMULATION_TOTAL_TIME_AND_COST) {
          const currentProbability = probabilityTypes[0];
          const x = calculateCombinedCost(estimate.simulationResult?.operation_costs?.costs[currentProbability]);
          const y = calculateCombinedCost(
            estimate.simulationResult?.operation_durations?.durations[currentProbability],
          );

          const pointColor = getLineColor(estimateIndex, estimates.length);

          return {
            borderColor: pointColor,
            pointBackgroundColor: pointColor,
            label: estimate.name,
            pointRadius: 10,
            pointHoverRadius: 12,
            data: [{ x, y: y / HOURS_PER_DAY }],
          };
        }

        return [];
      })
      .filter(Boolean);
  }

  const getXLabel = () => {
    if (chartType === simulationChartType.TOTAL_COST) {
      return '';
    }
    if (chartType === simulationChartType.SIMULATION_TOTAL_TIME_AND_COST) {
      return `${t(translations.costSetup_totalCost)} [${primaryCurrency}]`;
    }
    return timeCostType === timeCostView.TIME
      ? `${t(translations.time)} [${t(translations.days)}]`
      : `${t(translations.cost)} [${primaryCurrency}]`;
  };

  const getYLabel = () => {
    if (chartType === simulationChartType.TOTAL_COST) {
      return `${t(translations.cost)} [${primaryCurrency}]`;
    }
    if (chartType === simulationChartType.SIMULATION_TOTAL_TIME_AND_COST) {
      return `${t(translations.simulations_totalTime)} [${t(translations.days)}]`;
    }
    return chartType === simulationChartType.CUMULATIVE_PROBABILITY ||
      chartType === simulationChartType.CUMULATIVE_PROBABILITY_COST
      ? ''
      : t(translations.depth);
  };

  let content;

  let chartOptions = {
    controlsPortalId,
    data: {
      labels,
      datasets,
    },
    options: {
      chartStyling: {
        height: 650,
      },
      additionalAxesOptions: {
        reverse:
          chartType !== simulationChartType.TOTAL_COST &&
          chartType !== simulationChartType.SIMULATION_TOTAL_TIME_AND_COST,
      },
      axes: {
        x: [
          {
            label: getXLabel(),
          },
        ],
        y: [
          {
            label: getYLabel(),
          },
        ],
      },
      scales: {
        x: {
          ticks: {
            callback: (val) => {
              return displayNumber(val, { scientific: false });
            },
          },
        },
      },
      tooltip: {
        scientificNotation: false,
      },
    },
  };

  switch (chartType) {
    case simulationChartType.CUMULATIVE_PROBABILITY:
      content = (
        <LineChart
          chart={{
            ...chartOptions,
            options: {
              ...chartOptions.options,
              interactions: {
                onHover: handleOnHover,
              },
            },
          }}
        />
      );

      break;

    case simulationChartType.OPERATION_VS_DEPTH:
      content = <LineChart chart={chartOptions} />;

      break;

    case simulationChartType.TOTAL_COST:
      content = <BarChart chart={chartOptions} />;

      break;
    case simulationChartType.SIMULATION_TOTAL_TIME_AND_COST:
      chartOptions = {
        ...chartOptions,
        options: {
          ...chartOptions.options,
          chartStyling: {
            ...chartOptions.options.chartStyling,
            width: '80%',
          },
        },
      };

      content = <ScatterChart chart={chartOptions} />;

      break;

    default:
      content = <LineChart chart={chartOptions} />;

      break;
  }

  return content;
};

const mapStateToProps = ({ entities }) => {
  const primaryCurrency = selectPrimaryCurrency(entities);

  return {
    depthUnit: selectUnitByQuantity(entities, QuantitiesTypes.Depth),
    primaryCurrency,
  };
};

const Container = connect(mapStateToProps)(CombinedChart);

export { Container as CombinedChart };
